import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Col, Row, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup } from "@fortawesome/pro-duotone-svg-icons";
import { faBadgeCheck, faCheckCircle, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";

import { isObjectNotEmpty, moneyFormat, parseBoolOr, parseStringOr } from "../../helpers/AppxUtils";
import { getJsonSync } from "../../helpers/fetch";

import Price from "../Price";

import tvImg from "../../tv.png";
import netImg from "../../net.png";
import voiceImg from "../../voice.png";

import "./index.css";


export default function ChListByPackages({ theme }) {
    const { t }  = useTranslation('tvplan');
    const [ loading, setLoading ] = useState(true);
    const [ tvConfig, setTvConfig ] = useState(null);

    useEffect(() => {
        getJsonSync(theme.tvConfigFile)
            .then((response) => {
                let tvc = null;

                if (response?.data && response.data?.plans) {
                    tvc = response.data;
                }

                setTvConfig(tvc);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="list-by-package">
            {loading ? (
            <p>
                {t('Loading...')}
            </p>
            ) : tvConfig ? (
            <Fragment>
            <Plans tvConfig={tvConfig} />
            <Favs tvConfig={tvConfig} />
            <PickGroups tvConfig={tvConfig} />
            <Categories
                tvConfig={tvConfig}
                org={theme?.org||null}
            />
            </Fragment>
            ) : (
            <Alert
                type="error"
                message={t('Error fetching the TV Configuration')}
                showIcon={true}
            />
            )}
        </div>
    );
}

function Plans({ tvConfig }) {
    const { t, i18n }  = useTranslation('tvplan');
    const plans = tvConfig.plans || {};
    let sections = [];

    for (let planCode in plans) {
        const plan = plans[planCode];
        let des = i18n.language === 'fr' && plan.desFr ? plan.desFr : plan.des;

        if (!des) {
            des = planCode;
        }

        let services = {};

        for (let s of plan.services) {
            services[s] = true;
        }

        services = Object.keys(services);
        sections.push(
            <Col key={`plan-${planCode}`} xs={24} lg={12} xxl={8} className="print-break">
                <div className="tv-box">
                    <div className="tv-plan-name">
                        {des}
                        <div className="tv-plan-icons">
                            {plan.isNet > 0 && (<img src={netImg} alt="net-icon" />)}
                            {plan.isVoice > 0 && (<img src={voiceImg} alt="voice-icon" />)}
                            {plan.isTv > 0 && (<img src={tvImg} alt="tv-icon" />)}
                        </div>
                        <div className="tv-plan-price">
                            {moneyFormat(plan.price, plan.currency)}
                        </div>
                    </div>
                    <div className="tv-plan-services">
                        <div>
                            {services.map(code => (
                            <TvService
                                key={`key-plan-${planCode}-${code}`}
                                tvConfig={tvConfig}
                                code={code}
                            />
                            ))}
                        </div>
                    </div>
                </div>
            </Col>
        );
    }

    if (sections.length === 0) {
        sections.push(
            <div key="plan-no-package" className="no-packages">{t('No TV packages available')}</div>
        );
    }

    return (
        <div className="tv-section">
            <h2>{t('TV PACKAGES')}</h2>
            <Row gutter={[16, 16]}>
                {sections}
            </Row>
        </div>
    )
}

function Favs({ tvConfig }) {
    const { t, i18n }  = useTranslation('tvplan');
    const favs = tvConfig.favs || {};

    if (Object.keys(favs).length === 0) {
        return null;
    }

    let sections = [];

    for (let favCode in favs) {
        const fav = tvConfig.favs[favCode] || {};
        const services = fav.services || [];
        let des = parseStringOr(fav.des, "");
        let desFr = parseStringOr(fav.desFr, "");

        if (i18n.language === "fr" && desFr !== "") {
            des = desFr;
        }

        if (typeof fav !== "object") {
            continue;
        }

        sections.push(
            <Col key={`tv-channel-pack-fav-${favCode}`} xs={24} sm={12} lg={8}>
                <div className="tv-box">
                    <div className="tv-plan-name">
                        {des}
                        <div className="tv-plan-price" style={{ float: "right", fontSize: 18 }}>
                            {moneyFormat(fav.price, fav.currency)}
                        </div>
                    </div>
                    {services.length > 0 && services.map(s => (
                    <TvService
                        key={`key-fav-${favCode}-${s}`}
                        tvConfig={tvConfig}
                        code={s}
                    />
                    ))}
                </div>
            </Col>
        );
    }

    if (sections.length === 0) {
        return null;
    }

    return (
        <div className="tv-section">
            <h2>{t('THEME PACKAGES')}</h2>
            <Row gutter={[16, 16]}>
                {sections}
            </Row>
        </div>
    );
}

function PickGroups({ tvConfig }) {
    const { t, i18n }  = useTranslation('tvplan');
    const groups = tvConfig?.picks?.groups || [];

    if (!Array.isArray(groups)) {
        return null;
    }

    let sections = [];

    for (let groupCode of groups) {
        if (groupCode === "movies") {
            const group = tvConfig?.picks?.group?.[groupCode] || null;

            if (!isObjectNotEmpty(group)) {
                continue;
            }

            let des = parseStringOr(group.des, "Group");
            let desFr = parseStringOr(group.desFr, "");

            if (i18n.language === "fr" && desFr !== "") {
                des = desFr;
            }

            let services = [];

            if (Array.isArray(group?.services)) {
                for (let scode of group.services) {
                    if (scode !== "espnc") {
                        services.push(scode);
                    }
                }
            }

            sections.push(
                <Col key={`tv-channel-pack-group-${groupCode}`} xs={24} sm={12} lg={8}>
                    <div className="tv-box" style={{ textAlign: "left" }}>
                        <div className="tv-plan-name">
                            {des}
                        </div>
                        {services.length > 0 && services.map(s => (
                        <TvService
                            key={`key-group-${groupCode}-${s}`}
                            tvConfig={tvConfig}
                            code={s}
                            br={(group.br)}
                        />
                        ))}
                    </div>
                </Col>
            );
        }
    }

    if (sections.length === 0) {
        return null;
    }

    return (
        <div className="tv-section">
            <h2>{t('MOVIE PACKAGES')}</h2>
            <Row gutter={[16, 16]}>
                {sections}
            </Row>
        </div>
    );
}

function Categories({ tvConfig, org }) {
    const { t, i18n }  = useTranslation('tvplan');
    const groups = tvConfig?.picks?.groups || [];

    if (!Array.isArray(groups)) {
        return null;
    }

    const picksGroup = tvConfig?.picks?.group || {};
    const channels = tvConfig?.channels || {};
    let sections = [];
    let servicesDone = {};

    for (let groupCode of groups) {
        const group = picksGroup[groupCode] || null;

        if (!isObjectNotEmpty(group)) {
            continue;
        }

        for (let scode of group.services) {
            servicesDone[scode] = true;
        }

        if (groupCode === "movies") {
            continue;
        }

        let des = parseStringOr(group.des, "Group");
        let desFr = parseStringOr(group.desFr, "");

        if (i18n.language === "fr" && desFr !== "") {
            des = desFr;
        }

        let services = [];

        if (Array.isArray(group?.services)) {
            for (let scode of group.services) {
                if (scode == null) {
                    continue;
                }

                if (scode === "espnc") {
                    continue;
                }

                let sconfig = tvConfig?.service?.[scode] || null;

                if (sconfig == null) {
                    continue;
                }

                let sprice = sconfig.price || {};
                let pickPrice = sprice.pick || false;
                let saPrice = sprice.standalone || false;

                if (!pickPrice && !saPrice) {
                    continue;
                }

                let channel = channels[scode] || null;

                if (channel == null) {
                    continue;
                }

                services.push({
                    code: scode,
                    des: channel?.des || channel?.label || scode,
                    tileIcon: channel.tileIcon,
                    pickPrice: sprice.pick,
                    standalonePrice: saPrice,
                    minPickServices: parseInt(sconfig?.rights?.minPickServices) || 1,
                });
            }
        }

        sections.push(
            <div
                key={`tv-channel-group-${groupCode}`}
                className="tv-box"
                style={{ marginBottom: 16, textAlign: "left" }}
            >
                <div className="tv-plan-name">
                    {des}
                </div>
                {services.length > 0 && (
                <Table
                    pagination={false}
                    dataSource={services}
                    rowKey="code"
                    columns={[
                        {
                            dataIndex: "tileIcon",
                            title: t('Logo'),
                            align: "center",
                            render: (v) => (
                                <img
                                    src={`${process.env.PUBLIC_URL}/chicons/${v}`}
                                    alt="logo"
                                    height={20}
                                />
                            )
                        },
                        {
                            dataIndex: "des",
                            title: t('Name'),
                        },
                        {
                            dataIndex: "standalonePrice",
                            title: t('Standalone Price'),
                            render: (v, r) => moneyFormat(v, r.currency),
                        },
                        {
                            dataIndex: "minPickServices",
                            title: t('Group Discount'),
                            render: (v) => v + " " + t('channels'),
                        },
                        {
                            dataIndex: "pickPrice",
                            title: t('Group Price'),
                            render: (v, r) => moneyFormat(v, r.currency),
                        }
                    ]}
                />
                )}
            </div>
        );
    }

    const service = tvConfig?.service || {};
    let serviceCodes = [];

    for (let scode in service) {
        if (scode == null) {
            continue;
        }

        if (servicesDone[scode]) {
            continue;
        }

        if (org === "ctal" && (scode === "foxn" || scode === "planete")) {
            continue;
        }

        if (scode === "aside") {
            continue;
        }

        let sconfig = tvConfig?.service?.[scode] || {};
        let sprice = sconfig.price || {};
        let pickPrice = sprice.pick || false;
        let saPrice = sprice.standalone || false;

        if (!pickPrice && !saPrice) {
            continue;
        }

        let channel = channels[scode] || null;

        if (channel == null) {
            continue;
        }

        serviceCodes.push({
            code: scode,
            des: channel?.des || channel?.label || scode,
            tileIcon: channel.tileIcon,
            pickPrice: sprice.pick,
            standalonePrice: saPrice,
            minPickServices: parseInt(sconfig?.rights?.minPickServices) || 1,
        });
    }

    if (serviceCodes.length > 0) {
        serviceCodes.sort((a, b) => a.des.localeCompare(b.des));
        sections.push(
            <div
                key="key-group-others"
                className="tv-box"
                style={{ marginBottom: 16, textAlign: "left" }}
            >
                <div className="tv-plan-name">
                    {tvConfig?.pickOthersLabel || "Others"}
                </div>
                {serviceCodes.length > 0 && (
                <Table
                    pagination={false}
                    dataSource={serviceCodes}
                    rowKey="code"
                    columns={[
                        {
                            dataIndex: "tileIcon",
                            title: t('Logo'),
                            align: "center",
                            render: (v) => (
                                <img
                                    src={`${process.env.PUBLIC_URL}/chicons/${v}`}
                                    alt="logo"
                                    height={20}
                                />
                            )
                        },
                        {
                            dataIndex: "des",
                            title: t('Name'),
                        },
                        {
                            dataIndex: "standalonePrice",
                            title: t('Standalone Price'),
                            render: (v, r) => moneyFormat(v, r.currency),
                        },
                        {
                            dataIndex: "minPickServices",
                            title: t('Group Discount'),
                            render: (v) => v + " " + t('channels'),
                        },
                        {
                            dataIndex: "pickPrice",
                            title: t('Group Price'),
                            render: (v, r) => moneyFormat(v, r.currency),
                        }
                    ]}
                />
                )}
            </div>
        );
    }

    if (sections.length === 0) {
        return null;
    }

    return (
        <div className="tv-section">
            <h2>{t('Channels By Category')}</h2>
            <div>
                {sections}
            </div>
        </div>
    );
}

function TvService({ tvConfig, code, groupCode, showPlus, br, tileSize }) {
    const sconfig = tvConfig.service[code];
    //service not found...
    if (!isObjectNotEmpty(sconfig)) {
    	return (
            <div className="tv-plan-tv-service">
                <TvChannelTile
                    tvConfig={tvConfig}
                    code={code}
                    type="not-found"
                />
            </div>
        );
    }

    let ccount = 1;

    if (isObjectNotEmpty(sconfig?.channels)) {
        ccount = sconfig.channels.length;
    }

    let sprice = 0;

    if (isObjectNotEmpty(sconfig?.price)) {
        sprice = sconfig.price.standalone || 0;
    }

    let checkCode = null;

    if (ccount == 1) {
        return (
            <div className="tv-plan-tv-service">
                <TvChannelTile
                    tvConfig={tvConfig}
                    implicit={false}
                    size={tileSize||"small"}
                    code={code}
                    groupCode={groupCode}
                />
            </div>
        );
    }
    else {
    	let ret = [];

    	for(let i in sconfig.channels) {
            let ch = sconfig.channels[i];
            let hasPlus = false;

            if (sconfig.channels.length > 0 && i < (sconfig.channels.length -1)) {
                hasPlus = true;
            }

            let isMultiplex = true;

            if (i == 0) { isMultiplex = false; }

            let implicit = false;

            ret.push(
                <TvChannelTile
                    tvConfig={tvConfig}
                    key={`service-${code}-ch-${ch}`}
                    implicit={implicit}
                    size={tileSize||"small"}
                    isMultiplex={isMultiplex}
                    hasPlus={showPlus}
                    serviceCode={code}
                    groupCode={groupCode}
                    code={ch}
                />
            );
    	}

        let serviceClass = "tv-plan-tv-service";
        let containerClass = "tv-plan-tv-service-container";
        let priceCode = null;

        if (br == true) {
            containerClass += " br";
        }

        if (sconfig.showAsGroup == true) {
            serviceClass +=  " border";
            priceCode = (<div className="tv-plan-tv-service-sa-price"><Price>{sprice}</Price></div>);

            if (sprice < 0.01) {
                priceCode = null;
            }
        }

    	return (
            <div className={containerClass}>
                <div className="tv-plan-tv-service-content">
                    <div className={serviceClass}>
                        {priceCode}{ret}
                    </div>
                    {checkCode}
                </div>
            </div>
        );
    }
}

function TvChannelTile({ tvConfig, code, ...props }) {
    const serviceCode = props.serviceCode || code;
    const chr = tvConfig.channels[code] || {};
    const isMultiplex = parseBoolOr(props.isMultiplex, false);
    let tileIcon = "";

    if (isObjectNotEmpty(chr)) {
        tileIcon = parseStringOr(chr?.tileIcon, "");
    }

    let logoCode = code;

    if (tileIcon !== "") {
        logoCode = (<img src={`${process.env.PUBLIC_URL}/chicons/${tileIcon}`} alt="logo" />);
    }

    let tooltip = chr?.des || chr?.label || code;
    let tileClass = "tv-channel-tile";
    let containerClass = "tv-channel-tile-container";
    let hasPlusCode = null;

    if (props.hasPlus) {
        hasPlusCode = (
            <div className="tv-channel-tile-plus">
                <FontAwesomeIcon icon={faPlusCircle} />
            </div>
        );
    }

    if (props.size === "large") {
        tileClass += " large";
        containerClass += " large";
    }

    let svc = tvConfig.service[serviceCode] || {};

    if (!isObjectNotEmpty(svc)) {
        return (
            <div className={containerClass}>
                <div className={tileClass}>
                    <Tooltip placement="top" title={tooltip}>
                    <div className="tv-channel-tile-logo">
                     {logoCode}
                    </div>
                    </Tooltip>
                </div>
                {hasPlusCode}
            </div>
        );
    }

    let isStandalone = false;
    let isPick = false;
    let label = chr.label || "";
    let prices = svc.price || {};
    let pickPrice = parseFloat(prices?.pick) || 0;
    let saPrice = parseFloat(prices?.standalone) || 0;
    let rights = svc.rights || {};
    let minPickServices = rights.minPickServices || -1;
    // let canPick = false;

    // if (pickPrice > 0) {
    //     canPick = true;
    // }
    let labelCode = null;
    let saCode = null;

    if (label.length > 0) {
        labelCode = (<div className="tv-channel-tile-label">{label}</div>);
    }

    if (isMultiplex) {
        saPrice = 0;
        pickPrice = 0;
    }

    const showPrice = parseBoolOr(props?.showPrice, true);

    if (showPrice && props.size === "large" && saPrice > 0) {
        let saClass = 'tv-channel-tile-sa-price';

        if (isStandalone) {
            saClass += " standalone ";
        }

        if (isPick) {
            saClass += " pick ";
        }

        if (props.pickMode === "FIXED") {
            saClass += " tv-channel-tile-fixed ";
        }

        saCode = (<div className={saClass}><Price>{saPrice}</Price></div>);
    }

    let pickCode = null;
    let groupCount = null;

    if (props.pickMode !== "FIXED" && props.size === "large" && pickPrice > 0) {
        pickCode = (
            <div className="tv-channel-tile-pick-price">
                <Price>{pickPrice}</Price>
            </div>
        );
        groupCount = (
            <div className="tv-channel-tile-pick-group-count">
                <div className="stack-bg" />
                <div className='tv-channel-tile-stack-icon'>
                    <FontAwesomeIcon icon={faLayerGroup} />
                </div>
                <div className="tv-channel-pick-group-count-val">
                    {minPickServices}<span className="tv-channel-pick-group-count-plus">+</span>
                </div>
            </div>
        );
    }

    const showSelected = parseBoolOr(props.showSelected, true);
    let checkCode = null;

    if (showSelected) {
        const implicit = parseBoolOr(props.implicit, false);
        const groupCode = parseStringOr(props?.groupCode, "");

        if (implicit && groupCode === "") {
            tileClass += " implicit ";
            checkCode = (
                <div className="tv-channel-tile-imp-check">
                    <FontAwesomeIcon icon={faBadgeCheck} />
                </div>
            );
        }
        else {
            const selected = parseBoolOr(props.selected, false);

            if (selected) {
                let checkClass = "tv-channel-tile-check";

                if (isStandalone) {
                    tileClass += " selectedsa";
                    checkClass += " selectedsa";
                }
                else {
                    tileClass += " selected";
                }

                checkCode = (
                    <div className={checkClass}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                );
            }
        }
    }

    if (props.pickMode === "FIXED") {
        tileClass += " tv-channel-tile-fixed";
    }

    return (
        <div className={containerClass}>
            <div className={tileClass}>
                <Tooltip placement="top" title={tooltip}>
                    <div className="tv-channel-tile-logo">
                        {logoCode}
                        {labelCode}
                    </div>
                </Tooltip>
                {pickCode}
                {groupCount}
                {saCode}
            </div>
            {hasPlusCode}
            {checkCode}
        </div>
    );
}
